/**
 * Outreach Opportunities - Admin section for tracking outreach with POC info.
 * Active vs Archived (Ongoing/Ended).
 */
import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import { get, post, patch, del, getErrorMessage } from '../api/client';
import './OutreachPage.css';

const STATUS_OPTIONS = ['Planning', 'Meeting Scheduled', 'Agreement', 'Ongoing', 'Ended'];

const getDefaultForm = () => ({
  outreachOpportunity: '',
  crLead: '',
  pocLeadName: '',
  pocPosition: '',
  pocPhone: '',
  pocEmail: '',
  status: 'Planning',
  nextStep: '',
});

const OutreachPage = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(getDefaultForm());
  const [error, setError] = useState('');

  const fetchOpportunities = useCallback(async () => {
    const archived = activeTab === 'archived';
    const { ok, data } = await get(`/api/outreach?archived=${archived}`);
    if (ok && Array.isArray(data)) setOpportunities(data);
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    fetchOpportunities().finally(() => setLoading(false));
  }, [fetchOpportunities]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.outreachOpportunity?.trim()) {
      setError('Outreach Opportunity is required.');
      return;
    }
    setError('');
    try {
      const { ok, status, data } = await post('/api/outreach', form);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to add.'));
        return;
      }
      setAdding(false);
      setForm(getDefaultForm());
      await fetchOpportunities();
    } catch (err) {
      setError('Failed to add.');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    if (!form.outreachOpportunity?.trim()) {
      setError('Outreach Opportunity is required.');
      return;
    }
    setError('');
    try {
      const { ok, status, data } = await patch(`/api/outreach/${editingId}`, form);
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to update.'));
        return;
      }
      setEditingId(null);
      setForm(getDefaultForm());
      await fetchOpportunities();
    } catch (err) {
      setError('Failed to update.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this outreach opportunity?')) return;
    setError('');
    try {
      const { ok } = await del(`/api/outreach/${id}`);
      if (ok) {
        if (editingId === id) setEditingId(null);
        await fetchOpportunities();
      }
    } catch (err) {
      setError('Failed to delete.');
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry._id);
    setForm({
      outreachOpportunity: entry.outreachOpportunity || '',
      crLead: entry.crLead || '',
      pocLeadName: entry.pocLeadName || '',
      pocPosition: entry.pocPosition || '',
      pocPhone: entry.pocPhone || '',
      pocEmail: entry.pocEmail || '',
      status: entry.status || 'Planning',
      nextStep: entry.nextStep || '',
    });
  };

  return (
    <div className="outreach-page">
      <PageHeader title="Outreach Opportunities" section="outreach" />

      {error && <div className="outreach-error">{error}</div>}

      {/* Tabs */}
      <div className="outreach-tabs">
        <button
          className={`outreach-tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active
        </button>
        <button
          className={`outreach-tab ${activeTab === 'archived' ? 'active' : ''}`}
          onClick={() => setActiveTab('archived')}
        >
          Archived
        </button>
      </div>

      {/* Opportunities */}
      <section className="outreach-section outreach-opportunities">
        <div className="outreach-section-header">
          <h3 className="outreach-section-title">
            {activeTab === 'active' ? 'Active' : 'Archived'} Opportunities ({opportunities.length})
          </h3>
          {activeTab === 'active' && (
            <button
              className="outreach-btn-primary"
              onClick={() => { setAdding(true); setEditingId(null); setForm(getDefaultForm()); }}
            >
              Add Opportunity
            </button>
          )}
        </div>

        {loading && <p className="outreach-loading">Loading…</p>}

        {(adding || editingId) && (
          <form onSubmit={adding ? handleAdd : handleUpdate} className="outreach-form">
            <div className="outreach-form-grid">
              <div className="form-group form-group-full">
                <label>Outreach Opportunity *</label>
                <input
                  type="text"
                  value={form.outreachOpportunity}
                  onChange={e => setForm(p => ({ ...p, outreachOpportunity: e.target.value }))}
                  placeholder="Name/description"
                  required
                />
              </div>
              <div className="form-group">
                <label>CR Lead</label>
                <input
                  type="text"
                  value={form.crLead}
                  onChange={e => setForm(p => ({ ...p, crLead: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>POC Lead (First & Last Name)</label>
                <input
                  type="text"
                  value={form.pocLeadName}
                  onChange={e => setForm(p => ({ ...p, pocLeadName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>POC Position</label>
                <input
                  type="text"
                  value={form.pocPosition}
                  onChange={e => setForm(p => ({ ...p, pocPosition: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>POC Phone</label>
                <input
                  type="text"
                  value={form.pocPhone}
                  onChange={e => setForm(p => ({ ...p, pocPhone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>POC Email</label>
                <input
                  type="email"
                  value={form.pocEmail}
                  onChange={e => setForm(p => ({ ...p, pocEmail: e.target.value }))}
                />
              </div>
              <div className="form-group form-group-full">
                <label>Next Step</label>
                <input
                  type="text"
                  value={form.nextStep}
                  onChange={e => setForm(p => ({ ...p, nextStep: e.target.value }))}
                />
              </div>
            </div>
            <div className="outreach-form-actions">
              <button type="submit" className="outreach-btn-primary">{adding ? 'Add' : 'Save'}</button>
              <button type="button" className="outreach-btn-secondary" onClick={() => { setAdding(false); setEditingId(null); setForm(getDefaultForm()); }}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {!loading && opportunities.length === 0 && !adding && !editingId && (
          <p className="outreach-empty">No {activeTab} opportunities yet.</p>
        )}

        {!loading && opportunities.length > 0 && !adding && !editingId && (
          <div className="outreach-list">
            <div className="outreach-list-header">
              <span>Opportunity</span>
              <span>CR Lead</span>
              <span>POC</span>
              <span>Status</span>
              <span>Next Step</span>
              <span className="outreach-actions-col"></span>
            </div>
            {opportunities.map(entry => (
              <div key={entry._id} className="outreach-row">
                <div className="outreach-display" onClick={() => startEdit(entry)}>
                  <span className="outreach-opp">{entry.outreachOpportunity || '—'}</span>
                  <span className="outreach-crlead">{entry.crLead || '—'}</span>
                  <span className="outreach-poc">{entry.pocLeadName || '—'}</span>
                  <span className="outreach-status">{entry.status || '—'}</span>
                  <span className="outreach-next">{entry.nextStep || '—'}</span>
                  <span className="outreach-edit-hint">Tap to edit</span>
                </div>
                <button type="button" className="outreach-btn-delete outreach-row-delete" onClick={e => { e.stopPropagation(); handleDelete(entry._id); }} aria-label="Delete">Delete</button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default OutreachPage;
